import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import MainLayout from '../components/MainLayout';
import PageHeader from '../components/PageHeader';
import { FlowForm, FlowFormLeaveContext, useFlowFormGlobal } from 't-lib-ui/structure';
import { TextButton, Spinner, TabButtons } from 't-lib-ui/widgets';
import { FetchError } from 't-lib-ui/connector';
import { goTo, RouterContext } from 't-lib-ui/router';
import { nonNull } from 't-lib-ui/helpers';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { AppState } from '../context/general';
import FeedQuotaDialog from '../components/FeedQuotaDialog';
import { requestFeed, subscribeWithMode, updateFolders } from '../connector/api';
import {
  CachedFeedResult,
  RestFeedStatus,
  RestOpmlFeedEntry,
  RestOpmlImportResult,
  RestUserFeed,
  RestFolderUpdate,
} from '../schema-copy/api-schema';
import { isPrivateOnlyUrl, isRedditUrl, REDDIT_UNSUPPORTED_MESSAGE } from '../schema-copy/feed-privacy';
import { isSuperuser } from '../schema-copy/capabilities';
import { getPlanLimits } from '../schema-copy/userPlans';

const BATCH_SIZE = 20;
const MAX_FOLDERS = 25;

type RowStatus =
  | 'loading'
  | 'ready_public'        // user can choose public or private
  | 'ready_private_only'  // forced private (private-only URL)
  | 'auto_public'         // existing ACTIVE feed, not yet subscribed — auto-import as public
  | 'already_subscribed'
  | 'failed'
  | 'subscribing'
  | 'done'
  | 'skipped';

interface FeedRow {
  url: string;
  title: string;
  folder?: string;
  preferPrivate?: boolean;
  status: RowStatus;
  // 'skip' is a reversible user choice while the batch is under review;
  // confirmBatch promotes status to 'skipped' for rows still set to 'skip'.
  mode?: 'public' | 'private' | 'skip';
  existingFeedId?: string;
  errorMessage?: string;
  cachedFeed?: CachedFeedResult;
  resultFeedId?: string;
}

// Match AddFeed.pollForFeed timing — total ~40s before giving up.
const POLL_DELAYS = [3000, 2000, 2000, 2000, 3000, 3000, 4000, 5000, 5000, 5000, 5000];

/** Probe a single OPML URL: poll requestFeed until it resolves or we give up. */
async function probeFeedUrl(
  auth: any,
  url: string,
): Promise<
  | { kind: 'existing'; feed: RestUserFeed }
  | { kind: 'ready'; cached: CachedFeedResult }
  | { kind: 'failed'; message: string }
> {
  for (let attempt = 0; attempt <= POLL_DELAYS.length; attempt++) {
    if (attempt > 0)
      await new Promise(r => setTimeout(r, POLL_DELAYS[attempt - 1]));
    try {
      const result = await requestFeed(auth, url);
      if ('id' in result)
        return { kind: 'existing', feed: result as RestUserFeed };
      const status = (result as RestFeedStatus).feedStatus;
      if (status == 'READY') {
        const cached = (result as RestFeedStatus).cachedFeed;
        if (!cached) return { kind: 'failed', message: 'Feed metadata missing' };
        return { kind: 'ready', cached };
      }
      if (status == 'BAD' || status == 'BLOCK') {
        const errorType = (result as RestFeedStatus).errorType;
        return { kind: 'failed', message: errorType ? `Could not load (${errorType})` : 'Could not load feed' };
      }
      if (status == 'FETCHING') {
        // keep polling
      } else {
        // Unknown status (e.g. 'PRIVATE' from a feed not visible to this user). Treat as terminal so we don't loop.
        return { kind: 'failed', message: `Feed not available (${status})` };
      }
    } catch (e) {
      if (e instanceof FetchError && (e.type == 'FeedRejected' || e.type == 'InvalidRequest'))
        return { kind: 'failed', message: e.message || 'Feed rejected' };
      return { kind: 'failed', message: (e as Error).message };
    }
  }
  return { kind: 'failed', message: 'Timed out while checking the feed' };
}

const ImportOpmlPage: React.FC = () => {
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  useFlowFormGlobal();
  const isAdmin = isSuperuser(appState.state.authData?.selectedMembership?.capabilities);

  const [rows, setRows] = useState<FeedRow[] | null>(null);
  const [batchStart, setBatchStart] = useState(0);
  const [phase, setPhase] = useState<'init' | 'probing' | 'review' | 'subscribing' | 'finalizing' | 'done' | 'noData'>('init');
  const [showQuotaDialog, setShowQuotaDialog] = useState(false);
  const [globalError, setGlobalError] = useState<string | undefined>();

  // Load OPML data from sessionStorage on mount.
  // The ref guard makes this StrictMode-safe: in dev React mounts → unmounts → mounts again,
  // and without it the second run finds sessionStorage already cleared and falls into 'noData'.
  const consumedSessionStorage = useRef(false);
  useEffect(() => {
    if (consumedSessionStorage.current) return;
    consumedSessionStorage.current = true;
    const raw = sessionStorage.getItem('opmlImport');
    if (!raw) {
      setPhase('noData');
      return;
    }
    sessionStorage.removeItem('opmlImport');
    try {
      const parsed = JSON.parse(raw) as RestOpmlImportResult;
      const initialRows: FeedRow[] = parsed.feeds.map((f: RestOpmlFeedEntry) => ({
        url: f.url,
        title: f.title || f.url,
        folder: f.folder,
        preferPrivate: f.preferPrivate,
        status: 'loading',
      }));
      if (!initialRows.length) {
        setPhase('noData');
        return;
      }
      setRows(initialRows);
      setBatchStart(0);
      setPhase('probing');
    } catch {
      setPhase('noData');
    }
  }, []);

  const totalBatches = rows ? Math.ceil(rows.length / BATCH_SIZE) : 0;
  const currentBatchEnd = Math.min(batchStart + BATCH_SIZE, rows?.length ?? 0);
  const currentBatchIndices = useMemo(() => {
    const arr: number[] = [];
    for (let i = batchStart; i < currentBatchEnd; i++) arr.push(i);
    return arr;
  }, [batchStart, currentBatchEnd]);

  const updateRow = (idx: number, patch: Partial<FeedRow>) => {
    setRows(prev => {
      if (!prev) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  // Apply a probe result to a single row (used both during initial probe and on retry).
  const applyProbeResult = (
    idx: number,
    row: FeedRow,
    probed:
      | { kind: 'existing'; feed: RestUserFeed }
      | { kind: 'ready'; cached: CachedFeedResult }
      | { kind: 'failed'; message: string },
    subscribedSet: Set<string>,
  ) => {
    setRows(prev => {
      if (!prev) return prev;
      const next = [...prev];
      const r = { ...next[idx] };
      if (probed.kind == 'existing') {
        r.existingFeedId = probed.feed.id;
        if (subscribedSet.has(probed.feed.id)) {
          r.status = 'already_subscribed';
        } else if (probed.feed.feedStatus == 'PRIVATE') {
          // User's own private feed they unsubscribed from earlier — keep as private-only.
          r.status = 'ready_private_only';
          r.mode = 'private';
        } else {
          // Existing public feed the user isn't subscribed to: auto-import without asking.
          r.status = 'auto_public';
          r.mode = 'public';
        }
      } else if (probed.kind == 'ready') {
        r.cachedFeed = probed.cached;
        // Adult feeds may only be added privately — same rule as the URL-based private-only check.
        const forced = isPrivateOnlyUrl(row.url) || probed.cached.precheckFailure == 'adult';
        r.status = forced ? 'ready_private_only' : 'ready_public';
        r.mode = forced ? 'private' : (row.preferPrivate ? 'private' : 'public');
      } else {
        r.status = 'failed';
        r.errorMessage = probed.message;
      }
      next[idx] = r;
      return next;
    });
  };

  // Probe phase: when we enter 'probing', kick off requestFeed for the current batch in parallel.
  useEffect(() => {
    if (phase != 'probing' || !rows) return;
    let cancelled = false;
    const auth = appState.state.authData;
    if (!auth) return;
    const subscribedSet = new Set(appState.feedEngine.getFeeds().map(f => f.id));

    (async () => {
      await Promise.all(currentBatchIndices.map(async (idx) => {
        const row = rows[idx];
        // Reddit feeds can't be added by normal users — fail fast without probing the server.
        if (isRedditUrl(row.url) && !isAdmin) {
          if (cancelled) return;
          applyProbeResult(idx, row, { kind: 'failed', message: REDDIT_UNSUPPORTED_MESSAGE }, subscribedSet);
          return;
        }
        const probed = await probeFeedUrl(auth, row.url);
        if (cancelled) return;
        applyProbeResult(idx, row, probed, subscribedSet);
      }));
      if (!cancelled) setPhase('review');
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, batchStart]);

  const setRowMode = (idx: number, mode: 'public' | 'private' | 'skip') => updateRow(idx, { mode });

  const retryRow = async (idx: number) => {
    const auth = appState.state.authData;
    if (!auth || !rows) return;
    const row = rows[idx];
    const subscribedSet = new Set(appState.feedEngine.getFeeds().map(f => f.id));
    updateRow(idx, { status: 'loading', errorMessage: undefined });
    if (isRedditUrl(row.url) && !isAdmin) {
      applyProbeResult(idx, row, { kind: 'failed', message: REDDIT_UNSUPPORTED_MESSAGE }, subscribedSet);
      return;
    }
    const probed = await probeFeedUrl(auth, row.url);
    applyProbeResult(idx, row, probed, subscribedSet);
  };

  // ---------------- Quota tracking ----------------
  const userPlan = appState.state.currentUser?.userPlan ?? 'free';
  const { maxFeeds, maxPrivateFeeds } = getPlanLimits(userPlan);
  const currentFeedCount = appState.feedEngine.getFeeds().length;
  const currentPrivateCount = appState.feedEngine.getPrivateFeedCount();
  const totalRemaining = Math.max(0, maxFeeds - currentFeedCount);
  const privateRemaining = Math.max(0, maxPrivateFeeds - currentPrivateCount);

  // Count how many rows in the current batch will actually be subscribed at confirm time.
  // 'skip' is a reversible UI choice and never counts toward quota.
  // auto_public rows count too — they consume a public slot even though the user can't toggle them.
  const selectedInBatch = useMemo(() => {
    if (!rows) return { total: 0, private: 0 };
    let total = 0, priv = 0;
    for (const idx of currentBatchIndices) {
      const r = rows[idx];
      const counts = (r.status == 'ready_public' || r.status == 'ready_private_only' || r.status == 'auto_public')
        && (r.mode == 'public' || r.mode == 'private');
      if (!counts) continue;
      total++;
      if (r.mode == 'private') priv++;
    }
    return { total, private: priv };
  }, [rows, currentBatchIndices]);

  /** Whether the given row could be flipped to mode `target` without breaking quota. */
  const canSelect = (row: FeedRow, target: 'public' | 'private' | 'skip'): boolean => {
    if (row.mode == target) return true;
    // Skip never consumes quota.
    if (target == 'skip') return true;
    const wasCounted = row.mode == 'public' || row.mode == 'private';
    const totalAfter = selectedInBatch.total + (wasCounted ? 0 : 1);
    if (totalAfter > totalRemaining) return false;
    if (target == 'private') {
      const wasPrivate = row.mode == 'private';
      const privAfter = selectedInBatch.private + (wasPrivate ? 0 : 1);
      if (privAfter > privateRemaining) return false;
    }
    return true;
  };

  // Confirm the current batch: subscribe each selected row sequentially.
  const confirmBatch = async () => {
    if (!rows) return;
    const auth = nonNull(appState.state.authData);
    setPhase('subscribing');

    for (const idx of currentBatchIndices) {
      const row = rows[idx];
      if (row.status != 'ready_public' && row.status != 'ready_private_only' && row.status != 'auto_public') continue;
      // Promote the reversible 'skip' mode to a final 'skipped' status now that the user is confirming.
      if (row.mode == 'skip') {
        updateRow(idx, { status: 'skipped' });
        continue;
      }
      if (row.mode != 'public' && row.mode != 'private') continue;
      const subscribeMode = row.mode;
      updateRow(idx, { status: 'subscribing' });
      try {
        const result = await subscribeWithMode(auth, row.url, subscribeMode);
        appState.feedEngine.handleSubscribeResult(result);
        updateRow(idx, { status: 'done', resultFeedId: result.feed.id });
      } catch (e) {
        // The subscription already exists on the server (the probe missed it, or an earlier attempt
        // went through) — adopt it locally and count the row as done so folders still get applied.
        if (e instanceof FetchError && e.type == 'AlreadySubscribed' && e.extraData?.feedId) {
          const feedId = e.extraData.feedId as string;
          await appState.feedEngine.adoptExistingSubscription(feedId);
          updateRow(idx, { status: 'done', resultFeedId: feedId });
          continue;
        }
        if (e instanceof FetchError && e.type == 'Quota') {
          updateRow(idx, { status: 'failed', errorMessage: 'Plan quota reached' });
          setShowQuotaDialog(true);
          // Stop subscribing further rows in this batch — let the user see the dialog and decide.
          setPhase('review');
          return;
        }
        updateRow(idx, { status: 'failed', errorMessage: (e as Error).message });
      }
    }

    // Move to next batch or finalize
    const nextStart = batchStart + BATCH_SIZE;
    if (nextStart >= rows.length) {
      await finalize();
    } else {
      setBatchStart(nextStart);
      setPhase('probing');
    }
  };

  const finalize = async () => {
    setPhase('finalizing');
    setGlobalError(undefined);
    try {
      const auth = nonNull(appState.state.authData);
      const current = appState.feedEngine.getFolders();
      const update: RestFolderUpdate = { ...current };
      // Build a set of all feed ids already placed in any folder (named or unfiled), so we
      // don't double-place a feed that already lives somewhere.
      const placed = new Set<string>();
      for (const ids of Object.values(update)) for (const id of ids) placed.add(id);

      const namedKeys = () => Object.keys(update).filter(k => k);
      const allRows = rows ?? [];
      for (const r of allRows) {
        if (!r.folder) continue;
        if (r.status != 'done' || !r.resultFeedId) continue;
        if (placed.has(r.resultFeedId)) continue;
        if (!update[r.folder]) {
          // Cap named-folder count at MAX_FOLDERS — surplus stays unfiled.
          if (namedKeys().length >= MAX_FOLDERS) continue;
          update[r.folder] = [];
        }
        update[r.folder] = [...update[r.folder], r.resultFeedId];
        placed.add(r.resultFeedId);
      }

      const changed = JSON.stringify(update) != JSON.stringify(current);
      if (changed)
        await updateFolders(auth, update);
    } catch (e) {
      setGlobalError('Folder update failed: ' + (e as Error).message);
    }
    setPhase('done');
  };

  // Counts for the summary
  const counts = useMemo(() => {
    if (!rows) return { done: 0, alreadySubscribed: 0, failed: 0, skipped: 0 };
    let done = 0, alreadySubscribed = 0, failed = 0, skipped = 0;
    for (const r of rows) {
      if (r.status == 'done') done++;
      else if (r.status == 'already_subscribed') alreadySubscribed++;
      else if (r.status == 'failed') failed++;
      else if (r.status == 'skipped') skipped++;
    }
    return { done, alreadySubscribed, failed, skipped };
  }, [rows]);

  const batchHasLoading = rows
    ? currentBatchIndices.some(i => rows[i].status == 'loading')
    : false;

  const renderRow = (row: FeedRow, idx: number) => {
    const folderTag = row.folder ? <span className="opmlFolderTag">{row.folder}</span> : null;

    let right: React.ReactNode = null;
    if (row.status == 'loading') {
      right = (
        <div className="opmlRowStatus">
          <Spinner spinning={true} />
          <span className="opmlStatusInfo">Checking…</span>
        </div>
      );
    } else if (row.status == 'already_subscribed') {
      right = <span className="opmlStatusInfo">Already subscribed</span>;
    } else if (row.status == 'failed') {
      right = (
        <div className="opmlRowStatus">
          <span className="opmlStatusError" title={row.errorMessage}>{row.errorMessage || 'Failed'}</span>
          <TextButton label="Retry" type="minor" size="small" focussable="flow" onClick={() => retryRow(idx)} />
        </div>
      );
    } else if (row.status == 'subscribing') {
      right = (
        <div className="opmlRowStatus">
          <Spinner spinning={true} />
          <span className="opmlStatusInfo">Subscribing…</span>
        </div>
      );
    } else if (row.status == 'done') {
      right = <span className="opmlStatusOk">Imported</span>;
    } else if (row.status == 'skipped') {
      right = <span className="opmlStatusInfo">Skipped</span>;
    } else if (row.status == 'auto_public') {
      // Existing public feed: imports automatically, no user choice.
      right = <span className="opmlStatusInfo">Public — auto-import</span>;
    } else if (row.status == 'ready_public' || row.status == 'ready_private_only') {
      const lockedPrivate = row.status == 'ready_private_only';
      const labels = lockedPrivate ? ['Private', 'Skip'] : ['Public', 'Private', 'Skip'];
      // Map row.mode to TabButtons selectedIndex. Skip is a reversible selection until confirm.
      const selectedIndex = lockedPrivate
        ? (row.mode == 'private' ? 0 : row.mode == 'skip' ? 1 : -1)
        : (row.mode == 'public' ? 0 : row.mode == 'private' ? 1 : row.mode == 'skip' ? 2 : -1);
      const handleTabClick = (tabIdx: number) => {
        const target: 'public' | 'private' | 'skip' = lockedPrivate
          ? (tabIdx == 0 ? 'private' : 'skip')
          : (tabIdx == 0 ? 'public' : tabIdx == 1 ? 'private' : 'skip');
        if (canSelect(row, target)) setRowMode(idx, target);
      };
      right = (
        <div className="opmlRowStatus">
          {row.cachedFeed?.precheckFailure == 'adult' && (
            <span className="opmlStatusInfo">Adult content - private only</span>
          )}
          <TabButtons
            labels={labels}
            selectedIndex={selectedIndex}
            onClick={handleTabClick}
          />
        </div>
      );
    }
    return (
      <div key={idx} className="opmlRow">
        <div className="opmlRowMain">
          <div className="opmlRowTitle">
            {row.title}
            {folderTag}
          </div>
          <div className="opmlRowUrl">{row.url}</div>
        </div>
        <div className="opmlRowRight">{right}</div>
      </div>
    );
  };

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
      <MainLayout
        selectedFeed="account"
        right={({ onExpand }) => (
          <div className="opmlImportPage">
            <style>{`
              .opmlImportPage { width: 100%; height: 100%; overflow-y: auto; }
              .opmlImportContent { padding: 1rem 2rem; max-width: 900px; }
              .opmlBatchHeader { display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; margin: 0 0 0.5rem; font-size: 0.95rem; color: var(--gray-700, var(--gray-300)); }
              .opmlBatchHeader .opmlQuota { font-size: 0.875rem; color: var(--gray-600, var(--gray-400)); }
              .opmlRow { display: flex; gap: 1rem; padding: 0.5rem 0; border-bottom: 1px solid var(--border-color, var(--gray-925)); align-items: center; }
              .opmlRowMain { flex: 1; min-width: 0; }
              .opmlRowTitle { font-weight: 600; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
              .opmlRowUrl { font-size: 0.8125rem; color: var(--gray-500, var(--gray-500)); word-break: break-all; }
              .opmlFolderTag { display: inline-block; padding: 1px 8px; border-radius: 10px; background: var(--gray-100, var(--gray-950)); color: var(--gray-700, var(--gray-350)); font-size: 0.75rem; font-weight: 500; }
              .opmlRowRight { flex-shrink: 0; min-width: 280px; display: flex; justify-content: flex-end; align-items: center; }
              /* Make Private and Skip line up across rows (locked-private rows omit Public) by giving each tab a fixed minimum width. */
              .opmlRowRight .tabButtonsContainer > .textButton { min-width: 72px; }
              .opmlRowStatus { display: flex; align-items: center; gap: 0.5rem; }
              .opmlStatusInfo { font-size: 0.875rem; color: var(--gray-600, var(--gray-400)); }
              .opmlStatusError { font-size: 0.875rem; color: var(--error-color, #c33); max-width: 320px; overflow-wrap: anywhere; }
              .opmlStatusOk { font-size: 0.875rem; color: var(--success-color, #2a8); font-weight: 500; }
              .opmlActionBar { margin-top: 1rem; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
              .opmlSummary { display: flex; gap: 1.5rem; flex-wrap: wrap; margin: 1rem 0; font-size: 0.95rem; }
              .opmlSummary strong { font-size: 1.5rem; display: block; }
              @media (max-width: 750px) {
                .opmlImportContent { padding: 1rem; }
                .opmlRowUrl { display: none; }
                .opmlRow { flex-wrap: wrap; gap: 0.25rem 1rem; }
                .opmlRowMain { flex: 1 1 100%; }
                .opmlRowRight { min-width: 0; flex: 1; }
              }
            `}</style>
            <PageHeader title="Import OPML" onMenu={onExpand} onBack={() => goTo(routerCtx, '/a/account')} />
            <FlowForm main={true} className="opmlImportContent">
              {phase == 'init' && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}><Spinner spinning={true} /></div>
              )}
              {phase == 'noData' && (
                <div>
                  <p style={{ marginTop: 0 }}>No OPML import in progress.</p>
                  <TextButton label="Back to account" type="major" focussable="flow" onClick={() => goTo(routerCtx, '/a/account')} />
                </div>
              )}
              {(phase == 'probing' || phase == 'review' || phase == 'subscribing') && rows && (
                <>
                  <div className="opmlBatchHeader">
                    <span>
                      Batch {Math.floor(batchStart / BATCH_SIZE) + 1} of {totalBatches}
                      {' '}— feeds {batchStart + 1}–{currentBatchEnd} of {rows.length}
                    </span>
                    <span className="opmlQuota">
                      {totalRemaining - selectedInBatch.total} of {maxFeeds} feeds remaining
                      {' · '}
                      {privateRemaining - selectedInBatch.private} of {maxPrivateFeeds} private remaining
                    </span>
                  </div>
                  {currentBatchIndices.map(idx => renderRow(rows[idx], idx))}
                  <div className="opmlActionBar">
                    <TextButton
                      label={batchStart + BATCH_SIZE >= rows.length ? 'Confirm and finish' : 'Confirm batch'}
                      type="major"
                      focussable="flow"
                      enabled={phase == 'review' && !batchHasLoading ? 'enabled' : 'disabled'}
                      busy={phase == 'subscribing'}
                      onClick={confirmBatch}
                    />
                    <TextButton
                      label="Cancel import"
                      type="minor"
                      focussable="sideway"
                      enabled={phase != 'subscribing' ? 'enabled' : 'disabled'}
                      onClick={() => goTo(routerCtx, '/a/account')}
                    />
                  </div>
                </>
              )}
              {phase == 'finalizing' && (
                <div style={{ padding: '2rem 0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Spinner spinning={true} /> <span>Updating folders…</span>
                </div>
              )}
              {phase == 'done' && (
                <div>
                  <h3 style={{ margin: 0 }}>Import complete</h3>
                  {globalError && <p style={{ color: 'var(--error-color, #c33)' }}>{globalError}</p>}
                  <div className="opmlSummary">
                    <div><strong>{counts.done}</strong>imported</div>
                    <div><strong>{counts.alreadySubscribed}</strong>already subscribed</div>
                    <div><strong>{counts.skipped}</strong>skipped</div>
                    <div><strong>{counts.failed}</strong>failed</div>
                  </div>
                  <TextButton label="Done" type="major" focussable="flow" onClick={() => goTo(routerCtx, '/a/account')} />
                </div>
              )}
            </FlowForm>
            <FeedQuotaDialog show={showQuotaDialog} onClose={() => setShowQuotaDialog(false)} />
          </div>
        )}
        startWithLeftCollapsed={false}
      />
    </FlowFormLeaveContext.Provider>
  );
};

export default ImportOpmlPage;
